import { usePortal } from "@risalabs_frontend_org/oasis-ui-kit";
import { useCallback, useRef, useState } from "react";
import {
  getAllInsuranceDataForOrder,
  getPatientEligibilityDetails,
} from "../../../../api/bigQuery/nycbsPharmaOrders";
import { InfoIcon } from "../../../../svg/info-icon";
import { computeInsuranceInfoTooltipStyle } from "./utils/computeInsuranceInfoTooltipStyle";
import {
  getPbmTooltipSummary,
  PbmTooltipSummary,
} from "./utils/getInsuranceFieldDistinctCounts";

interface InsuranceInfoIconCellProps {
  /** CMM order identifier used for eligibility and insurance API calls. */
  value: string;
}

/**
 * Table cell that fetches insurance/eligibility details on hover and logs responses.
 * Shows a portal tooltip with loading text, then PBM count and insurance names.
 */
const InsuranceInfoIconCell = ({ value }: InsuranceInfoIconCellProps) => {
  const portal = usePortal();
  const iconRef = useRef<HTMLButtonElement>(null);
  const hoverRequestIdRef = useRef(0);
  const [showTooltip, setShowTooltip] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [pbmSummary, setPbmSummary] = useState<PbmTooltipSummary>({
    count: 0,
    names: [],
  });

  const handleMouseEnter = useCallback(async () => {
    if (!value) {
      return;
    }

    const requestId = hoverRequestIdRef.current + 1;
    hoverRequestIdRef.current = requestId;
    setShowTooltip(true);
    setIsLoading(true);
    setPbmSummary({ count: 0, names: [] });

    try {
      const [eligibilityDetails, insuranceDetails] = await Promise.all([
        getPatientEligibilityDetails(value),
        getAllInsuranceDataForOrder(value),
      ]);

      if (hoverRequestIdRef.current !== requestId) {
        return;
      }

      setPbmSummary(getPbmTooltipSummary(insuranceDetails, eligibilityDetails));
    } catch (error) {
      if (hoverRequestIdRef.current !== requestId) {
        return;
      }
      setPbmSummary({ count: 0, names: [] });
    } finally {
      if (hoverRequestIdRef.current === requestId) {
        setIsLoading(false);
      }
    }
  }, [value]);

  const handleMouseLeave = useCallback(() => {
    hoverRequestIdRef.current += 1;
    setShowTooltip(false);
    setIsLoading(false);
    setPbmSummary({ count: 0, names: [] });
  }, []);

  const handleClick = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      event.stopPropagation();
    },
    [],
  );

  const anchor = iconRef.current;
  const tooltipStyle =
    anchor != null
      ? computeInsuranceInfoTooltipStyle(anchor.getBoundingClientRect())
      : undefined;

  const tooltipContent = isLoading ? (
    <span className="italic">Loading...</span>
  ) : pbmSummary.count > 0 ? (
    <div className="flex flex-col gap-1">
      <div className="whitespace-nowrap font-semibold">
        PBMs: {pbmSummary.count}
      </div>
      {pbmSummary.names.map((name) => (
        <div key={name} className="break-words text-primaryGray-1">
          {name}
        </div>
      ))}
    </div>
  ) : (
    <span>No PBM data</span>
  );

  return (
    <>
      <div className="flex justify-center">
        <button
          ref={iconRef}
          type="button"
          className="flex cursor-pointer items-center justify-center rounded border-none bg-transparent p-1 hover:bg-primaryGray-16"
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          onClick={handleClick}
          aria-label="View insurance and eligibility details"
        >
          <InfoIcon stroke="#0056D6" />
        </button>
      </div>
      {showTooltip &&
        tooltipStyle &&
        portal(
          <div
            style={tooltipStyle}
            role="tooltip"
            className="rounded-md border border-primaryGray-16 bg-white p-2 text-tiny shadow-lg"
          >
            {tooltipContent}
          </div>,
        )}
    </>
  );
};

export default InsuranceInfoIconCell;
